import { IAddControlNotificationOptions, IColumnInfo, IDataProvider, IFieldChange, IFieldValidationResult, IRecord } from "../interfaces";
import { Field } from "./Field/Field";
import { RecordUi } from "./RecordUi";

export class Record implements IRecord {
    private _recordId: string;
    private _primaryName: string;
    private _dataProvider: IDataProvider;
    private _initialRecordData: { [key: string]: any };
    private _fields: Map<string, Field> = new Map();
    private _ui: RecordUi;

    constructor(
        recordId: string,
        dataProvider: IDataProvider,
    ) {
        this._dataProvider = dataProvider;
        this._recordId = recordId;
        this._primaryName = dataProvider.getColumns().find(x => x.isPrimary)?.name ?? this._dataProvider.getMetadata()?.PrimaryNameAttribute ?? "unknown";
        this._initialRecordData = this._dataProvider.getRawRecordData(recordId) ?? {};
        this._ui = new RecordUi(this._dataProvider, (columnName: string) => this._getField(columnName));
    }

    public isValid(): boolean {
        return !this._dataProvider.getColumns().some(x => !this._getField(x.name).isValid());
    }
    public getFormattedValue(columnName: string): string | null {
        return this._getField(columnName)!.getFormattedValue();
    }
    public getRecordId(): string {
        return this._recordId;
    }
    public getValue(columnName: string): any {
        if (this._dataProvider.getColumns().find(col => col.name === columnName)) {
            return this._getField(columnName)!.getValue();
        }
        console.warn(`Column ${columnName} does not exists!`);
        return null;
    }
    public setValue(columnName: string, value: any) {
        const field = this._getField(columnName)!;
        field.setValue(value);

    }
    public getNamedReference(): ComponentFramework.EntityReference {
        return {
            id: {
                guid: this.getRecordId()
            },
            name: this.getRawData()[this._primaryName],
            etn: this._dataProvider.getMetadata()?.LogicalName ?? "entityName"
        };
    }

    //if he have some onSave listeners, use their save logic instead of the provider one
    public async save(): Promise<void> {
        const savePromises = this._dataProvider.fireEventListeners('onRecordSave', this);
        if (savePromises.length > 0) {
            await Promise.all(savePromises);
        }
        else {
            await this._dataProvider.save(this);
        }
        this._dataProvider.fireEventListeners('onAfterRecordSaved', this);
    }

    public getRawData() {
        //if no data is present (we have loaded to other page), fallback to cached initial record data
        return this._dataProvider.getRawRecordData(this.getRecordId()) ?? this._initialRecordData;
    }

    public getCurrencySymbol(columnName: string): string {
        //try to get currency symbol through expression, if not provided, fallback to provider
        return this._getField(columnName).getCurrencySymbol() ?? this._dataProvider.getRecordCurrencySymbol(this, columnName);
    }

    public isDirty(columnName?: string) {
        if (columnName) {
            return this._getField(columnName).isDirty();
        }
        const fields = this._dataProvider.getColumns().map(x => this._getField(x.name));
        return !!fields.find(x => x.isDirty());
    }

    public getChanges(columnName?: string): IFieldChange[] {
        if (columnName) {
            const change = this._getField(columnName).getChange();
            return change ? [change] : [];
        }
        const fields = this._dataProvider.getColumns().map(x => this._getField(x.name));
        const changes = fields.flatMap(x => x.getChange()).filter(y => y !== null);
        //todo: typescript fuckery
        return changes as IFieldChange[];
    }

    public clearChanges(columnName?: string) {
        if (columnName) {
            const change = this._getField(columnName).getChange();
            if (change) {
                this.setValue(columnName, change.originalValue);
            }
        }
        else {
            const fields = this._dataProvider.getColumns().map(x => this._getField(x.name));
            fields.map(field => {
                const change = field.getChange();
                if (change) {
                    this.setValue(field.getColumn().name, change.originalValue);
                }
            });
        }
    }

    public getColumnInfo(columnName: string): IColumnInfo {
        return this._getField(columnName).getColumnInfo();
    }

    public get expressions(): IRecord['expressions'] {
        return {
            setCurrencySymbolExpression: (columnName: string, currencySymbolExpression: () => string) => {
                this._getField(columnName).setFieldProperty('currencySymbolExpression', currencySymbolExpression);
            },
            ui: {
                setNotificationsExpression: (columnName: string, notificationsExpression: () => IAddControlNotificationOptions[]) => {
                    this._getField(columnName).setFieldProperty('notificationsExpression', notificationsExpression);
                },
                setLoadingExpression: (columnName: string, loadingExpression: () => boolean) => {
                    this._getField(columnName).setFieldProperty('loadingExpression', loadingExpression);
                },
                setHeightExpression: (columnName: string, heightExpression: (columnWidth: number, rowHeight: number) => number | undefined) => {
                    this._getField(columnName).setFieldProperty('heightExpression', heightExpression);
                },
                setCellEditorParametersExpression: (columnName: string, parameterExpression: (parameters: any) => any) => {
                    this._getField(columnName).setFieldProperty('cellEditorParametersExpression', parameterExpression);
                }

            },
            setValidationExpression: (columnName: string, validationExpression: () => IFieldValidationResult) => {
                this._getField(columnName).setFieldProperty('validationExpression', validationExpression);
            },
            setValueExpression: (columnName: string, valueExpression: () => any) => {
                this._getField(columnName).setFieldProperty('valueExpression', valueExpression);
            },
            setDisabledExpression: (columnName: string, disabledExpression: () => boolean) => {
                this._getField(columnName).setFieldProperty('disabledExpression', disabledExpression);
            },
            setRequiredLevelExpression: (columnName: string, requiredLevelExpression: () => Xrm.Attributes.RequirementLevel) => {
                this._getField(columnName).setFieldProperty('requiredLevelExpression', requiredLevelExpression);
            }
        };
    }

    public get ui() {
        return this._ui;
    }

    private _getField(columnName: string): Field {
        if (!this._fields.get(columnName)) {
            this._fields.set(columnName, new Field(
                this,
                columnName,
                this._dataProvider,
            ));
        }
        return this._fields.get(columnName)!;
    }
}